/** Until insights uses mp-line-chart any changes in here will also
 * need to be made in iron/apps/insights/src/insights-app/insights-result/chart-display/line-chart/mp-line-chart/mixins/annotations.js */
import moment from 'moment-timezone';
import sortedLastIndex from 'lodash/sortedLastIndex';

import {getProjectId, getReportName, getWorkspaceId} from 'common/report/util';
import {annotationsClient} from 'common/report/queries/app-api/annotations-client';
import {getMpContext} from 'common/mp-context';
import {offsetForHighcharts} from 'common/util/date';
import * as colors from 'common/util/colors';
import {formatDate} from 'common/util/date';

import {Annotation, AnnotationState} from '../types';
import {AnnotationEvent, AnnotationLocationDetails, ButtonLocationDetails, HighChartXAxis, LineChartEl} from './type';
import {track, TrackingEvent, TrackingEventProperty} from './tracking';

export const EXISTING_ANNOTATIONS_BUTTON_Y = -4;
export const ANNOTATION_PLOT_LINE_ID = `annotation-plot-line`;
export const HOVER_PLOT_LINE_ID = `hover-plot-line`;
export const CREATE_ANNOTATION_ID = `create-annotation-id`;
export const ANNOTATION_GROUP_BUTTON_ID_PREFIX = `annotation-group-button`;

export class Annotations {
  private ticks: string[];

  constructor(public lineChart: LineChartEl) {}

  getHelpers(): any {
    return {
      add: ({detail: annotation}: AnnotationEvent) => {
        this.process([annotation]);
        this.closePopup();
      },
      close: () => {
        this.closePopup();
      },
      delete: ({detail: annotation}: AnnotationEvent) => this.updateLineChartState({deletingAnnotation: annotation}),
      deleteCanceled: () => this.updateLineChartState({deletingAnnotation: null}),
      deleteConfirmed: async () => {
        const projectId = parseInt(getProjectId());
        const workspaceId = parseInt(getWorkspaceId());
        const timeIndex = this.lineChart.state.annotations.activeAnnotationButton.timestampMs;
        const {deletingAnnotation} = this.lineChart.state.annotations;

        await annotationsClient.deleteAnnotation({projectId, id: deletingAnnotation.id, workspaceId});

        const annotationRemaining = this.lineChart.state.annotations.allAnnotations
          .get(timeIndex)
          .filter((annotation) => annotation.id !== deletingAnnotation.id);

        const allAnnotations = this.lineChart.state.annotations.allAnnotations;

        if (annotationRemaining.length === 0) {
          allAnnotations.delete(timeIndex);
        } else {
          allAnnotations.set(timeIndex, annotationRemaining);
        }

        this.updateLineChartState({allAnnotations});

        this.setAnnotations();

        if (annotationRemaining.length === 0) {
          this.closePopup();
        }

        this.updateLineChartState({deletingAnnotation: null});

        track(TrackingEvent.Delete, {
          [TrackingEventProperty.AnnotationText]: deletingAnnotation.description,
          [TrackingEventProperty.AnnotationTimestamp]: moment(deletingAnnotation.date).format(`YYYY-MM-DD HH:mm:ss`),
          [TrackingEventProperty.ReportName]: getReportName(),
        });
      },
      edit: ({detail: {editingAnnotation, annotationDescriptionDraft}}) => {
        this.updateLineChartState({editingAnnotation, annotationDescriptionDraft});
      },
      editCanceled: () => this.updateLineChartState({editingAnnotation: null}),
      popupChange: (ev) => {
        if (ev.detail && ev.detail.state === `closed`) {
          this.popupClosed();
        }
      },
      save: async () => {
        const {editingAnnotation} = this.lineChart.state.annotations;
        if (editingAnnotation) {
          const {allAnnotations, annotationDescriptionDraft} = this.lineChart.state.annotations;
          const projectId = parseInt(getProjectId());
          const workspaceId = parseInt(getWorkspaceId());
          const {id} = editingAnnotation;
          await annotationsClient.updateAnnotation({
            projectId,
            id,
            description: annotationDescriptionDraft,
            workspaceId,
          });
          editingAnnotation.description = annotationDescriptionDraft;

          // Update the annotation that we've saved to show immediately
          for (const key of Object.keys(allAnnotations)) {
            for (let i = 0; i < allAnnotations[key].length; i++) {
              const entry = allAnnotations[key][i];
              if (entry.id === id) {
                allAnnotations[key][i].description = annotationDescriptionDraft;
              }
            }
          }

          this.updateLineChartState({editingAnnotation: null, allAnnotations});
          this.setAnnotations();

          track(TrackingEvent.Edit, {
            [TrackingEventProperty.AnnotationText]: editingAnnotation.description,
            [TrackingEventProperty.AnnotationTimestamp]: moment(editingAnnotation.date).format(`YYYY-MM-DD HH:mm:ss`),
            [TrackingEventProperty.ReportName]: getReportName(),
          });
        }
      },
      showCreate: () => this.showCreateAnnotation(this.lineChart.state.annotations.activeAnnotationButton),
      updateDescriptionDraft: ({detail}) => this.updateLineChartState({annotationDescriptionDraft: detail}),
    };
  }

  getDefaultState(): any {
    return {
      activeAnnotationButton: null,
      creatingAnnotation: false,
      activeAnnotationPopupClosing: false,
      createAnnotationDate: ``,
      visibleAnnotations: new Map<number, any>(),
      allAnnotations: new Map<number, any>(),
      activeAnnotationButtonTimestamp: null,
      editingAnnotation: null,
      viewingAnnotations: false,
      activeAnnotationPreview: null,
      annotationDescriptionDraft: null,
      deletingAnnotation: null,
    };
  }

  closePopup() {
    this.updateLineChartState({activeAnnotationPopupClosing: true});
  }

  generateTickMarks() {
    this.ticks = Object.keys(this.lineChart.getHighcharts().axes[0].ticks);
  }

  getXAxisMinAndMax(): HighChartXAxis {
    const ticks = this.lineChart.getHighcharts().xAxis[0].tickPositions;
    const minDateMs = parseInt(ticks[0]);
    let maxDateMs = parseInt(ticks[ticks.length - 1]);

    if (minDateMs === maxDateMs) {
      maxDateMs = moment(minDateMs).add(1, this.lineChart.attr(`trend-unit`)).valueOf();
    }

    return {minDateMs, maxDateMs};
  }

  async load() {
    const xAxis = this.getXAxisMinAndMax();
    const formatDate = (timestamp) => moment(timestamp).tz(getMpContext().projectTimezone).format(`YYYY-MM-DD`);
    const annotations = await annotationsClient.query({
      projectId: parseInt(getProjectId()),
      fromDate: formatDate(xAxis.minDateMs),
      toDate: formatDate(xAxis.maxDateMs),
      workspaceId: getMpContext().workspaceId,
    });

    this.process(annotations);
  }

  locationDetails(annotation: AnnotationLocationDetails, timestampMs: number) {
    const bbox = annotation.labelsGroup.getBBox();
    const topOfButtonToBottomOfChart = this.lineChart.getHighcharts().chartHeight - bbox.y;

    return {
      ...bbox,
      formattedDate: this.timestampToTimeUnitFunction()(timestampMs),
      timestampMs,
      topOfButtonToBottomOfChart,
    };
  }

  popupClosed() {
    this.updateLineChartState({
      viewingAnnotations: false,
      activeAnnotationButton: null,
      creatingAnnotation: false,
      activeAnnotationPopupClosing: false,
    });
  }

  /**
   * Group annotations by the nearest chart timestamp that is less than the
   * annotation timestamp, sort them in ascending order, and update the app state
   */
  process(annotationsToProcess: Annotation[]) {
    const utcOffset = getMpContext().utcOffset;
    const projectTimezone = getMpContext().projectTimezone;

    const allAnnotations = this.lineChart.state.annotations.allAnnotations;

    annotationsToProcess.forEach((annotation) => {
      annotation.highchartsDate = offsetForHighcharts(moment(annotation.date), projectTimezone, utcOffset);

      const annotationDateMS = moment.utc(annotation.highchartsDate).valueOf();
      const index = parseInt(this.ticks[sortedLastIndex(this.ticks, annotationDateMS.toString()) - 1]);

      if (allAnnotations.has(index)) {
        const annotationsAtTime = allAnnotations.get(index);
        annotationsAtTime.push(annotation);
        allAnnotations.set(index, annotationsAtTime);
      } else {
        allAnnotations.set(index, [annotation]);
      }
    });

    this.updateLineChartState({allAnnotations});
    this.setAnnotations();
  }

  setAnnotations(minDateMs?: number, maxDateMs?: number) {
    const xAxis = !(minDateMs || maxDateMs) ? this.getXAxisMinAndMax() : {minDateMs, maxDateMs};
    const visibleAnnotations = new Map(this.lineChart.state.annotations.allAnnotations);

    visibleAnnotations.forEach((_, date) => {
      if (date < xAxis.minDateMs || date > xAxis.maxDateMs) {
        visibleAnnotations.delete(date);
      }
    });

    this.updateLineChartState({visibleAnnotations});
    this.redrawGroupButtons();
  }

  redrawCreateButton() {
    if (this.lineChart.attr(`download-mode`)) return;

    this.removePlotLine(HOVER_PLOT_LINE_ID);
    this.removeElements((id) => id === CREATE_ANNOTATION_ID);

    if (
      this.lineChart.attr(`show-annotations`) &&
      this.lineChart.getHoveredTimestampMs() &&
      !this.lineChart.state.annotations.visibleAnnotations.has(this.lineChart.getHoveredTimestampMs())
    ) {
      this.renderCreateAnnotationButton(this.lineChart.getHoveredTimestampMs(), false);
      this.renderPlotLine(this.lineChart.getHoveredTimestampMs(), HOVER_PLOT_LINE_ID);
    }
  }

  redrawGroupButtons() {
    if (this.lineChart.attr(`download-mode`)) return;

    this.removeElements((id) => id.includes(ANNOTATION_GROUP_BUTTON_ID_PREFIX));
    this.removePlotLine(ANNOTATION_PLOT_LINE_ID);

    this.lineChart.state.annotations.visibleAnnotations.forEach((annotations, timestampMsStr) => {
      const timestampMs = Number(timestampMsStr);
      this.renderViewAnnotationsButton(
        timestampMs,
        this.lineChart.state.annotations.activeAnnotationButtonTimestamp === timestampMs,
        annotations.length,
      );
      this.renderPlotLine(timestampMs, ANNOTATION_PLOT_LINE_ID);
    });
  }

  removeElements(matcher = (_) => true) {
    if (this.lineChart.getHighcharts() && this.lineChart.getHighcharts().annotations) {
      [...this.lineChart.getHighcharts().annotations].forEach((annotation) => {
        if (matcher(annotation.userOptions.id)) {
          this.lineChart.getHighcharts().removeAnnotation(annotation.userOptions.id);
        }
      });
    }
  }

  removePlotLine(plotLineId: string) {
    const xAxis = this.lineChart.getHighcharts().xAxis[0];
    xAxis.removePlotLine(plotLineId);
  }

  renderPlotLine(timestampMs: number, plotLineId: string) {
    const xAxis = this.lineChart.getHighcharts().xAxis[0];
    xAxis.addPlotLine({
      value: timestampMs,
      width: 1,
      color: colors.mpColors[`$gray2`],
      id: plotLineId,
    });
  }

  renderCreateAnnotationButton(timestampMs: number, active: boolean) {
    const annotation = this.lineChart.getHighcharts().addAnnotation({
      id: CREATE_ANNOTATION_ID,
      labels: [
        {
          borderWidth: 0,
          backgroundColor: `rgba(255,255,255,0)`,
          shape: `rect`,
          padding: 0,
          point: {
            x: timestampMs,
            y: this.lineChart.getHighcharts().yAxis[0].min,
            xAxis: 0,
            yAxis: 0,
          },
          useHTML: true,
          verticalAlign: `top`,
          text: `<mp-tooltip placement="top">Add Annotation</mp-tooltip><create-annotation-button active="${active}" />`,
          y: this.lineChart.state.annotations.visibleAnnotations.size ? EXISTING_ANNOTATIONS_BUTTON_Y : 8,
        },
      ],
    });

    annotation.labels[0].on(`click`, () => {
      const detail = this.locationDetails(annotation, timestampMs);
      this.showCreateAnnotation(detail);
    });

    return annotation;
  }

  renderViewAnnotationsButton(timestampMs: number, active: boolean, numAnnotations: number) {
    const annotation = this.lineChart.getHighcharts().addAnnotation({
      id: `${ANNOTATION_GROUP_BUTTON_ID_PREFIX}-${timestampMs}`,
      labels: [
        {
          allowOverlap: true,
          borderWidth: 0,
          backgroundColor: `rgba(255,255,255,0)`,
          shape: `rect`,
          padding: 0,
          point: {
            x: timestampMs,
            y: this.lineChart.getHighcharts().yAxis[0].min,
            xAxis: 0,
            yAxis: 0,
          },
          useHTML: true,
          verticalAlign: `top`,
          text: `<view-annotations-button id="view-annotations-button-${timestampMs}" active="${active}" num-annotations="${numAnnotations}" />`,
          y: EXISTING_ANNOTATIONS_BUTTON_Y,
        },
      ],
    });

    annotation.labels[0].on(`click`, () => {
      const detail = this.locationDetails(annotation, timestampMs);
      this.viewAnnotationsClicked(detail);
    });

    annotation.labels[0].on(`mouseenter`, () => {
      const detail = this.locationDetails(annotation, timestampMs);
      this.updateLineChartState({activeAnnotationPreview: detail});
    });

    annotation.labels[0].on(`mouseleave`, () => {
      this.updateLineChartState({activeAnnotationPreview: null});
    });
  }

  timestampToTimeUnitFunction({displayRangeIfWeek = true} = {}) {
    const unit = this.lineChart.attr(`trend-unit`);
    const customFormatting = {day: `ddd, MMM D`};
    return (timestamp) => formatDate(timestamp, {unit, displayRangeIfWeek, customFormatting});
  }

  showCreateAnnotation(activeAnnotationButtonLocationDetails: ButtonLocationDetails) {
    const date = moment.unix(activeAnnotationButtonLocationDetails.timestampMs / 1000);
    this.updateLineChartState({
      creatingAnnotation: true,
      createAnnotationDate: date.format(`MMM D, YYYY HH:mm:ss`),
      activeAnnotationButton: activeAnnotationButtonLocationDetails,
      viewingAnnotations: false,
    });
  }

  updateLineChartState(state: Partial<AnnotationState>) {
    const annotations = Object.assign({}, this.lineChart.state.annotations, state);
    this.lineChart.update({annotations});
  }

  viewAnnotationsClicked(detail: ButtonLocationDetails) {
    this.updateLineChartState({
      creatingAnnotation: false,
      viewingAnnotations: true,
      activeAnnotationButton: detail,
      activeAnnotationPreview: null,
    });

    const activeAnnotationGroup = this.lineChart.state.annotations.visibleAnnotations.get(
      this.lineChart.state.annotations.activeAnnotationButton.timestampMs,
    );

    track(TrackingEvent.Read, {
      [TrackingEventProperty.AnnotationText]: activeAnnotationGroup
        .map((annotation) => annotation.description)
        .join(`, `),
      [TrackingEventProperty.AnnotationTimestamp]: moment(activeAnnotationGroup[0].date).format(`YYYY-MM-DD HH:mm:ss`),
      [TrackingEventProperty.ReportName]: getReportName(),
      [TrackingEventProperty.AnnotationCount]: activeAnnotationGroup.length,
    });
  }
}
